export interface FrameMetrics {
  durationMs: number;
  frames: number;
  fps: number;
  medianFrameMs: number;
  p95FrameMs: number;
  p99FrameMs: number;
  maxFrameMs: number;
  framesOver33Ms: number;
  framesOver100Ms: number;
  longFrames: number;
}

export interface RenderWorkMetrics {
  calls: number;
  durationMs: number;
  medianMs: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  overBudgetCalls: number;
}

export type TvSupportTier =
  | "below-baseline"
  | "canvas-baseline"
  | "canvas-enhanced"
  | "webgl2-capable"
  | "webgl2-enhanced";

export interface BenchmarkClassificationInput {
  canvas: FrameMetrics;
  webgl2: FrameMetrics | null;
  webgl2Representative?: FrameMetrics | null;
  fuseRunners?: {
    compatibility: FrameMetrics;
    rich: FrameMetrics;
    compatibilityRenderWork?: RenderWorkMetrics;
    richRenderWork?: RenderWorkMetrics;
  };
}

export interface BenchmarkClassification {
  tier: TvSupportTier;
  title: string;
  summary: string;
  meetsMinimum: boolean;
  recommendedFusePreset: "compatibility" | "canvas-rich";
  fuseRunnersCompatibilityPass: boolean | null;
  fuseRunnersRichPass: boolean | null;
}

export function summarizeFrameTimes(frameTimes: readonly number[]): FrameMetrics {
  const usable = frameTimes.filter((value) => Number.isFinite(value) && value > 0);
  if (usable.length === 0) {
    return {
      durationMs: 0,
      frames: 0,
      fps: 0,
      medianFrameMs: 0,
      p95FrameMs: 0,
      p99FrameMs: 0,
      maxFrameMs: 0,
      framesOver33Ms: 0,
      framesOver100Ms: 0,
      longFrames: 0,
    };
  }
  const sorted = usable.slice().sort((a, b) => a - b);
  const durationMs = usable.reduce((total, value) => total + value, 0);
  return {
    durationMs: round(durationMs),
    frames: usable.length,
    fps: round((usable.length * 1000) / durationMs),
    medianFrameMs: round(percentile(sorted, 0.5)),
    p95FrameMs: round(percentile(sorted, 0.95)),
    p99FrameMs: round(percentile(sorted, 0.99)),
    maxFrameMs: round(sorted[sorted.length - 1] ?? 0),
    framesOver33Ms: usable.filter((value) => value > 1000 / 30).length,
    framesOver100Ms: usable.filter((value) => value >= 100).length,
    longFrames: usable.filter((value) => value >= 50).length,
  };
}

export function classifyTvSupport(input: BenchmarkClassificationInput): BenchmarkClassification {
  const canvasMinimum = meets30FpsFloor(input.canvas);
  const canvasEnhanced = meets60FpsTarget(input.canvas);
  const webglEvidence =
    input.webgl2Representative === undefined ? input.webgl2 : input.webgl2Representative;
  const webglMinimum = webglEvidence ? meets30FpsFloor(webglEvidence) : false;
  const webglEnhanced = webglEvidence ? meets60FpsTarget(webglEvidence) : false;
  const richFramePass = input.fuseRunners ? meets30FpsFloor(input.fuseRunners.rich) : false;
  const richRenderPass =
    !input.fuseRunners?.richRenderWork || meetsRenderWorkBudget(input.fuseRunners.richRenderWork);
  const recommendedFusePreset =
    input.fuseRunners && richFramePass && richRenderPass ? "canvas-rich" : "compatibility";
  const fuseRunnersCompatibilityPass = input.fuseRunners
    ? meets30FpsFloor(input.fuseRunners.compatibility) &&
      (!input.fuseRunners.compatibilityRenderWork ||
        meetsRenderWorkBudget(input.fuseRunners.compatibilityRenderWork))
    : null;
  const fuseRunnersRichPass = input.fuseRunners ? richFramePass && richRenderPass : null;

  if (!canvasMinimum) {
    return {
      tier: "below-baseline",
      title: "Below the current TV graphics baseline",
      summary:
        "This TV did not sustain the required 720p Canvas workload at approximately 30 fps. Use reduced effects or a lower internal resolution before claiming support.",
      meetsMinimum: false,
      recommendedFusePreset,
      fuseRunnersCompatibilityPass,
      fuseRunnersRichPass,
    };
  }
  if (webglEnhanced) {
    return {
      tier: "webgl2-enhanced",
      title: "WebGL2 enhanced graphics candidate",
      summary:
        "Canvas meets the required baseline and the representative WebGL2 scene sustained the enhanced target. A 10-minute physical gameplay soak and context-recovery test are still required before enabling WebGL2 by default.",
      meetsMinimum: true,
      recommendedFusePreset,
      fuseRunnersCompatibilityPass,
      fuseRunnersRichPass,
    };
  }
  if (webglMinimum) {
    return {
      tier: "webgl2-capable",
      title: "WebGL2 capable",
      summary:
        "Canvas meets the required baseline and the representative WebGL2 scene sustained approximately 30 fps. Prefer conservative effects until a 10-minute physical gameplay soak proves more headroom.",
      meetsMinimum: true,
      recommendedFusePreset,
      fuseRunnersCompatibilityPass,
      fuseRunnersRichPass,
    };
  }
  if (canvasEnhanced) {
    return {
      tier: "canvas-enhanced",
      title: "Enhanced Canvas graphics",
      summary:
        "The required Canvas workload has enough measured headroom for richer particles, animation, or a carefully tested higher resolution. WebGL2 is unavailable or below its performance floor.",
      meetsMinimum: true,
      recommendedFusePreset,
      fuseRunnersCompatibilityPass,
      fuseRunnersRichPass,
    };
  }
  return {
    tier: "canvas-baseline",
    title: "Canvas compatibility graphics",
    summary:
      "This TV meets the current minimum 720p Canvas requirement. Use the compatibility preset and keep optional effects bounded.",
    meetsMinimum: true,
    recommendedFusePreset,
    fuseRunnersCompatibilityPass,
    fuseRunnersRichPass,
  };
}

export function summarizeRenderWork(renderTimes: readonly number[]): RenderWorkMetrics {
  const usable = renderTimes.filter((value) => Number.isFinite(value) && value >= 0);
  if (usable.length === 0) {
    return {
      calls: 0,
      durationMs: 0,
      medianMs: 0,
      p95Ms: 0,
      p99Ms: 0,
      maxMs: 0,
      overBudgetCalls: 0,
    };
  }
  const sorted = usable.slice().sort((a, b) => a - b);
  return {
    calls: usable.length,
    durationMs: round(usable.reduce((total, value) => total + value, 0)),
    medianMs: round(percentile(sorted, 0.5)),
    p95Ms: round(percentile(sorted, 0.95)),
    p99Ms: round(percentile(sorted, 0.99)),
    maxMs: round(sorted[sorted.length - 1] ?? 0),
    overBudgetCalls: usable.filter((value) => value > 28).length,
  };
}

function meets30FpsFloor(metrics: FrameMetrics): boolean {
  return metrics.fps >= 27 && metrics.p95FrameMs <= 40;
}

function meets60FpsTarget(metrics: FrameMetrics): boolean {
  return metrics.fps >= 55 && metrics.p95FrameMs <= 22;
}

function meetsRenderWorkBudget(metrics: RenderWorkMetrics): boolean {
  return metrics.p95Ms <= 28 && metrics.maxMs <= 80;
}

function percentile(sorted: readonly number[], ratio: number): number {
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * ratio) - 1));
  return sorted[index] ?? 0;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
